import { logger } from '@accedo/logger';
import { config } from '##/services/store/store';
import { getDeviceInformation, getIpAddress } from '##/utils/device';
import { Messengers } from '##/components/Player/utils/Messengers';
import { getCachedFMSResponse } from '##/services/tenApi/fms';

type AdParams = {
  ge: number;
  gr: number;
  ppid: string;
  postcode: string;
  timeStamp: number;
  region: string;
  deviceId: string;
  is_lat: string;
  idtype: string;
};

export const getAdParameters = async (): Promise<AdParams> => {
  const { ge, gr, n10adId, postcode, memberId } = config.$get().user || {};
  const { region } = config.$get().geo;
  const ppid = n10adId || memberId || '';
  const timeStamp = Math.floor(Date.now() / 1000);

  const { deviceId } = await getDeviceInformation();

  const is_lat = '0';
  const idtype = 'dpid';

  return {
    ge,
    gr,
    ppid,
    postcode,
    timeStamp,
    region,
    deviceId,
    is_lat,
    idtype,
  };
};

const assembleBVODParamsString = (): string => {
  const bvodResponseParams = Messengers.setBVODParams$.getCurrentValue();

  if (!bvodResponseParams?.length) {
    return '';
  }

  return bvodResponseParams?.reduce((acc, curr, idx) => {
    const { BvodId, BvodIdSalt, Demo } = curr || {};

    const count = idx + 1;

    const bvodIdSaltValue =
      BvodIdSalt?.toLowerCase() === 'none' || !BvodIdSalt ? '' : BvodIdSalt;

    return `${acc}&oztam_${count}=${BvodId}&oztam_${count}salted=${bvodIdSaltValue}&oztam_${count}demo=${Demo}`;
  }, '');
};

const getFreewheelBVODParams = (isLive: boolean): string => {
  const bvodResponseParams = Messengers.setBVODParams$.getCurrentValue();

  if (!bvodResponseParams?.length) {
    return '';
  }

  return bvodResponseParams?.reduce((acc, curr, idx) => {
    const { BvodId, BvodIdSalt, Demo } = curr || {};

    const count = idx + 1;

    const bvodIdSaltValue =
      BvodIdSalt?.toLowerCase() === 'none' || !BvodIdSalt ? '' : BvodIdSalt;

    const oztamBvodKey = isLive ? `imafw_oztam_${count}` : `oztam_${count}`;
    const oztamBvodSaltKey = isLive
      ? `imafw_oztam_${count}salted`
      : `oztam_${count}salted`;
    const oztamDemoKey = isLive
      ? `imafw_oztam_${count}demo`
      : `oztam_${count}demo`;

    const oztamKeyValue = {
      [oztamBvodKey]: BvodId,
      [oztamBvodSaltKey]: bvodIdSaltValue,
      [oztamDemoKey]: Demo,
    };

    let oztamString = '';

    Object.keys(oztamKeyValue).forEach((key) => {
      const value = oztamKeyValue[key as keyof typeof oztamKeyValue];
      if (value !== 'none') {
        oztamString += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    return `${acc}${oztamString}`;
  }, '');
};

const getDIdValue = async (): Promise<string> => {
  const { deviceId } = await getAdParameters();

  let deviceType = '';
  let fieldName = 'imafw__fw_did';

  if (process.env.APP_PLATFORM === 'vidaa') {
    deviceType = 'vidaa';
  } else if (process.env.APP_PLATFORM === 'lg') {
    deviceType = 'lgudid';
  } else if (process.env.APP_PLATFORM === 'tizen') {
    deviceType = 'tifa';
  } else if (process.env.APP_PLATFORM === 'hbbtv') {
    deviceType = 'ctv';
    fieldName = '_fw_did';
  } else {
    deviceType = 'ctv';
  }

  return `&${fieldName}=${deviceType}:${deviceId}`;
};

const deleteHardcodedBvodAdTagParams = (adUrl: string): string => {
  try {
    // delete all placeholders and append these values later
    const adParams = new URLSearchParams(adUrl);

    for (let idx = 1; idx < 5; idx += 1) {
      adParams.delete(`imafw_oztam_${idx}`);
      adParams.delete(`imafw_oztam_${idx}salted`);
      adParams.delete(`imafw_oztam_${idx}demo`);
    }

    return decodeURI(adParams.toString());
  } catch (err) {
    return adUrl;
  }
};

const isLatFieldName = 'imafw__fw_is_lat';

export const buildFreewheelAdUrl = async (
  urlFrame: string,
  genre: string | undefined,
  ozTamSessionId: string,
  authToken: string,
  assetId: string,
  isLive: boolean,
  fwSiteSectionId?: string,
): Promise<string> => {
  let adUrl =
    process.env.APP_PLATFORM === 'hbbtv'
      ? urlFrame
      : deleteHardcodedBvodAdTagParams(urlFrame);

  let tfcd = 0;

  if (!isLive) {
    tfcd = genre?.toLowerCase() === 'kids' ? 1 : 0;
  }

  if (isLive && fwSiteSectionId) {
    adUrl = adUrl.replace(/\[FWSITESECTIONID\]/g, fwSiteSectionId);
  }

  const { ge, gr, postcode, deviceId, is_lat } = await getAdParameters();
  const { state } = await getIpAddress();
  adUrl = adUrl.replace(/\[GE\]/g, ge !== null ? String(ge) : '');
  adUrl = adUrl.replace(/\[GR\]/g, gr !== null ? String(gr) : '');
  adUrl = adUrl.replace(/\[STATE\]/g, state !== null ? String(state) : '');

  if (!adUrl.includes('[STATE]')) {
    adUrl += `&state=${state}`;
  }

  if (!adUrl.includes('TFCD')) {
    adUrl += `&_fw_coppa=${tfcd}`;
  }

  adUrl = adUrl.replace(
    /\[PCODE\]/g,
    postcode !== null ? String(postcode) : '',
  );

  if (postcode) {
    adUrl += `&imafw__fw_h_x_postal_code=${String(postcode)}`;
  }

  if (process.env.APP_PLATFORM === 'hbbtv') {
    adUrl += `&${isLatFieldName.replace('imafw_', '')}=${is_lat}`;
  } else {
    adUrl += `&${isLatFieldName}=${is_lat}`;
  }

  const fmsValues = getCachedFMSResponse()?.fms_params;
  const fwBvodParams = getFreewheelBVODParams(isLive);

  const custParams = {
    ...fmsValues, // imafw__fw_vcid2, imafw__fw_3P_UID etc.
    genre,
  };

  Object.keys(custParams).forEach((key) => {
    const value = String(custParams[key as keyof typeof custParams]);
    adUrl += `&${key.replace('imafw_', '')}=${encodeURIComponent(value)}`;
  });

  if (isLive) {
    adUrl = adUrl.replace('\r\n ', '');
    adUrl = adUrl.replace(';', '&');
    adUrl = adUrl.replace(':', `${encodeURIComponent(':')}`);

    // global params
    adUrl = adUrl.replace(/\[TFCD\]/g, String(tfcd));
    adUrl = adUrl.replace(
      /\[SUB\]/g,
      fmsValues ? String(fmsValues.imafw_fms_userid) : '',
    );
    adUrl = adUrl.replace(
      /\[OZTAM_SESSION_ID\]/g,
      ozTamSessionId !== null ? String(ozTamSessionId) : '',
    );
    adUrl = adUrl.replace(
      /\[OZTAMSESSION\]/g,
      ozTamSessionId !== null ? String(ozTamSessionId) : '',
    );
    adUrl = adUrl.replace('%%ADVERTISING_IDENTIFIER_PLAIN%%', deviceId); // &rdid=
    adUrl = adUrl.replace('%%ADVERTISING_IDENTIFIER_IS_LAT%%', is_lat); // &is_lat=
    adUrl += `&imafw__fw_h_x_country=AU&imafw__fw_content_id=${assetId}`;
    adUrl += '&imafw_mode=LIVE&imafw_vdty=VARIABLE';
    adUrl += fwBvodParams;

    if (authToken) {
      adUrl += `&auth-token=${encodeURIComponent(authToken)}`;
    }
  } else {
    // just need to append fwBvodParams in right area
    adUrl = adUrl.replace(
      /\[GRAB_VALUE_FROM_imafw_fms_userid\]/g,
      fmsValues ? `${String(fmsValues.imafw_fms_userid) + fwBvodParams} ` : '',
    );
    adUrl = adUrl.replace(/\[SAME_AS_GAM_VID\]/g, `${String(assetId)}`);
    adUrl = adUrl.replace(
      /\[OZTAM_SESSION\]/g,
      ozTamSessionId !== null ? String(ozTamSessionId) : '',
    );
    adUrl = adUrl.replace('vmap1+vast4', 'vmap1');
  }

  const dIdValueParam = await getDIdValue();

  adUrl += dIdValueParam;

  return adUrl;
};

export const buildGoogleAdUrl = async (
  urlFrame: string,
  videoAltId: string | undefined,
  genre: string | undefined,
  ozTamSessionId?: string,
  isCSAI = false,
): Promise<string> => {
  let adUrl = urlFrame;
  const tfcd = genre?.toLowerCase() === 'kids' ? 1 : 0;
  const {
    ge,
    gr,
    ppid,
    timeStamp,
    region,
    postcode,
    deviceId,
    is_lat,
    idtype,
  } = await getAdParameters();

  adUrl = adUrl.replace(/\[TFCD\]/g, String(tfcd));
  adUrl = adUrl.replace(/\[PPID\]/g, ppid ?? '');
  adUrl = adUrl.replace(/\[GE\]/g, ge !== null ? String(ge) : '');
  adUrl = adUrl.replace(/\[GR\]/g, gr !== null ? String(gr) : '');
  adUrl = adUrl.replace(
    '&vid=[VID]',
    videoAltId != null ? `&vid=${videoAltId}` : '',
  );
  if (isCSAI) {
    adUrl = adUrl.replace('xml_vast2', 'xml_vmap1');
  }

  adUrl = adUrl.replace('=[TIMESTAMP]', `=${timeStamp}`);
  adUrl = adUrl.replace('%%ADVERTISING_IDENTIFIER_PLAIN%%', deviceId); // &rdid=
  adUrl = adUrl.replace('%%ADVERTISING_IDENTIFIER_IS_LAT%%', is_lat); // &is_lat=
  adUrl = adUrl.replace('%%ADVERTISING_IDENTIFIER_TYPE%%', idtype); // &idtype=

  if (ozTamSessionId) {
    if (adUrl.match(/\[OZTAM_SESSION_ID\]/g)) {
      adUrl = adUrl.replace(/\[OZTAM_SESSION_ID\]/g, ozTamSessionId);
    } else {
      adUrl += `%26session%3D${ozTamSessionId}`;
    }
  } else {
    logger.error('OZTAMSESSIONID NOT SET!');
  }

  if (
    ['nsw', 'wa', 'act', 'nt', 'sa', 'tas', 'vic', 'qld'].includes(
      region.toLowerCase(),
    )
  ) {
    adUrl += `%26state%3D${region.toLowerCase()}`;
  }

  if (postcode && /^\d{4}$/.exec(postcode)) {
    adUrl += `%26pcode%3D${postcode}`;
  }

  const bvodResponseParams = Messengers.setBVODParams$.getCurrentValue();

  if (bvodResponseParams?.length) {
    adUrl += encodeURIComponent(assembleBVODParamsString());
    adUrl += encodeURIComponent(`&oztamozsid=${ozTamSessionId}`);
  }

  return adUrl;
};
